/**
 * E2E BROWSER test probe — drives the four REAL standalone-web journey stages
 * (ENG-4330) through their real domain trackers, via
 * `globalThis.__irisWebFunnelProbe`.
 *
 * WHY A BROWSER PROBE EXISTS AT ALL (read this before deleting it or replacing
 * it with a UI interaction):
 *
 * `client` is the ONE dimension the standalone-web funnel is read on, and it is
 * not a per-call property. `PostHogManager.captureInternal` spreads
 * `getClientProperties()` LAST over every caller's bag, and that method resolves
 * `isElectronApp() ? 'electron' : 'standalone-web'` with no surface parameter and
 * no seam (posthog/manager.ts). `isElectronApp()` is computed once at module load
 * from `window.electron`, so in the Electron Playwright lane every event that goes
 * through a domain tracker is `client=electron`, permanently — no probe, no
 * injection and no click path can change that. The ONLY way to observe the web
 * funnel on the real emit path is to load the real bundle in a real browser, where
 * there is no preload and the surface genuinely resolves to web.
 *
 * A browser cannot reach the room UI: an unauthenticated boot redirects to Auth0,
 * and the account then meets a mandatory hosted onboarding prompt. So the stages
 * are driven directly through the same domain trackers the product call sites use.
 *
 * WHAT IS REAL AND WHAT IS SUPPLIED. Supplied: the device identity (a device id,
 * brand id and model id) and the auth-journey source — the same class of input a
 * real user's hardware and login supply, and nothing that the funnel's claim
 * depends on. Real: every domain tracker (boot / auth / device / media), the dual
 * writes, `track()`, the device-prop convention gate, event validation, envelope
 * enrichment, `client` resolution, the `before_send` chain, and the ingest POST.
 * `client=standalone-web` in particular is derived by the shipped code from the
 * absence of a preload — it is never handed in. That is the whole point: a receipt
 * that injected `client` would stay green if the product stopped resolving the
 * surface at all.
 *
 * THE GATE — three conditions, all required, because this probe emits journey
 * events and a funnel inflated by a probe is worse than no funnel:
 *   1. No `window.electron` — so it can NEVER install in any Electron renderer,
 *      shipped or e2e. The desktop funnel has its own coverage.
 *   2. A loopback hostname — so a real standalone-web deployment (a real https
 *      host) can never install it whatever globals a page carries. This is the
 *      condition that makes production structurally unreachable rather than
 *      merely unlikely.
 *   3. `globalThis.__IRIS_E2E_WEB_FUNNEL_HARNESS__ === true`, strictly — set by
 *      the Playwright harness through `addInitScript`, which runs before any page
 *      script. Strict `=== true` rather than truthiness for the reason
 *      download-origin-probe's own gate is `=== '1'`: a probe that self-installs
 *      on a coincidental value is a one-call trigger for a real event.
 *
 * Per `think-in-systems` (ai.knowledge/global): a primitive that exposes the
 * emits the receipt asserts on, never a re-implementation of them.
 * Per `stay-in-scope` (ai.knowledge/global): journey and shadow methods for the
 * browser surface whose delivery receipts require a real standalone-web runtime.
 * Sibling precedent: download-origin-probe.ts (ENG-4328), web-feature-gate-probe.ts
 * (ENG-4332) — same test-only intent, same single-statement global assignment.
 */
import { config } from '#~/config/config.js';
import { track } from '#~/analytics/track.js';
import type { AnalyticsEvent } from '#~/analytics/types/events.js';
import type { AnalyticsTrackFn } from '#~/analytics/domains/types.js';
import { beginAuthJourney } from '#~/auth/auth-journey.js';
import { createBootLifecycleTracker } from '#~/analytics/domains/boot-lifecycle.js';
import { createAuthJourneyTracker } from '#~/analytics/domains/auth-journey.js';
import { createDeviceLifecycleTracker } from '#~/analytics/domains/device-lifecycle.js';
import { createMediaControlsDiscoveryTracker } from '#~/analytics/domains/media-controls-discovery-lifecycle.js';
import { WEB_FUNNEL_STAGE_EVENTS } from '#~/analytics/constants/web-funnel.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { nextShadowKind, type ShadowKind } from '#~/analytics/domains/shadow-kind.js';
import {
  WEB_FUNNEL_HARNESS_GLOBAL,
  isBrowserE2eEnabled,
  type WebFunnelGateScope
} from '#~/test-probes/web-funnel-harness.js';

/** The device identity a caller supplies, mirroring what real hardware supplies. */
export interface WebFunnelDeviceIdentity {
  deviceId: string;
  deviceBrandId: string;
  deviceModelId: string;
  brandName: string;
  modelName: string;
}

export interface WebFunnelDriveResult {
  /**
   * The canonical stage events driven, in stage order.
   *
   * Returned so the receipt can prove WHICH stages this probe drove instead of
   * inferring it from rows that happened to arrive — an empty or short list is a
   * harness failure, and a harness failure must not read as a delivery failure.
   */
  stages: string[];
  /** Echoed back so the receipt asserts against the identity it supplied. */
  device: WebFunnelDeviceIdentity;
}

export interface WebFunnelProbe {
  /**
   * Drive all four standalone-web journey stages once each, in journey order.
   *
   * @returns null when the env/vendor context is absent, which is the same guard
   *          the product call sites emit behind. A silent no-op would otherwise be
   *          indistinguishable from an event lost between the tracker and PostHog,
   *          which is exactly the ambiguity the receipt exists to remove.
   */
  driveJourney(device: WebFunnelDeviceIdentity): WebFunnelDriveResult | null;
  /** Exercise profile-persistent shadow classification through the real web send path. */
  driveShadow(
    device: WebFunnelDeviceIdentity,
    agentId: string,
    studioId: string
  ): Promise<{ shadowKind: ShadowKind } | null>;
}

// The gate lives in a dependency-free module so the PostHog manager can label this
// lane's sends from the same fact; re-exported so existing importers are unchanged.
export { WEB_FUNNEL_HARNESS_GLOBAL, isBrowserE2eEnabled, type WebFunnelGateScope };

/**
 * The (name, category, properties) adapter every domain tracker takes, bound to
 * the module-level `track` — the identical function `AnalyticsContext.Provider`
 * hands components. Copied from download-origin-probe.ts, including the `as`:
 * `AnalyticsEvent` couples each name to its category, so a caller holding a
 * widened `EventName` cannot narrow to a union member.
 */
const trackDomainEvent: AnalyticsTrackFn = (name, category, properties) => {
  void track({ name, category, properties, timestamp: Date.now() } as AnalyticsEvent);
};

/**
 * Build the probe.
 *
 * Exported separately from the install below so its behaviour can be unit-tested
 * with ONE module import: re-evaluating this module per case (the pattern
 * download-origin-probe.test.ts uses) pulls four domain-tracker graphs through
 * jest's ESM loader and trips "Module cache already has entry" — a jest bug, not a
 * property of the probe. The gate is tested through `isBrowserE2eEnabled` for the
 * same reason.
 */
export const createWebFunnelProbe = (): WebFunnelProbe => {
  return {
    async driveShadow(device, agentId, studioId) {
      if (!config.env || !config.vendor) return null;

      // The fixture supplies the same camera/catalog bag as Broker. Classification,
      // profile persistence, enrichment and ingestion remain the shipped functions;
      // the real Broker consumer is covered separately by its bootstrap receipt.
      // Same Studio-scoped once-flag key as the Broker bootstrap.
      const shadowKind = await nextShadowKind(device.deviceId, studioId);
      trackDomainEvent(EVENT_NAMES.DEVICE_SHADOWED, EVENT_CATEGORIES.DEVICE, {
        device_id: device.deviceId,
        agent_id: agentId,
        studio_id: studioId,
        target_device_id: device.deviceId,
        target_bridge_id: agentId,
        source_device_id: device.deviceId,
        device_brand_id: device.deviceBrandId,
        device_model_id: device.deviceModelId,
        brand_name: device.brandName,
        model_name: device.modelName,
        is_embedded: true,
        shadow_kind: shadowKind
      });
      return { shadowKind };
    },
    driveJourney(device: WebFunnelDeviceIdentity): WebFunnelDriveResult | null {
      if (!config.env || !config.vendor) {
        return null;
      }

      createBootLifecycleTracker(trackDomainEvent).trackBootSucceeded({
        boot_duration_ms: 0
      });

      // Stage 2 — sign-in. `beginAuthJourney` is the product's own journey
      // minter (auth/auth-journey.ts), so the journey id and source are produced
      // the way the real Auth0 round trip produces them; auth/provider.tsx:72
      // then hands that journey to this same tracker.
      const journey = beginAuthJourney('studio_login');
      createAuthJourneyTracker(trackDomainEvent).trackLoginSucceeded(journey);

      // Stage 3 — add camera. Mirrors video/components/add/wiring.ts:189 with the
      // canonical device cohort props only; `device_brand`/`device_model` are
      // off-convention and are rejected by assertCanonicalDeviceProps.
      createDeviceLifecycleTracker(trackDomainEvent).trackDeviceAddSuccess({
        device_id: device.deviceId,
        device_brand_id: device.deviceBrandId,
        device_model_id: device.deviceModelId,
        brand_name: device.brandName,
        model_name: device.modelName
      });

      // Stage 4 — first stream. Mirrors the web add-device surface at
      // video/components/add/source/discovery.tsx:223 (`surface: 'add_device'`),
      // not the room-lifecycle observer: a browser reaches the wizard surface.
      createMediaControlsDiscoveryTracker(trackDomainEvent).trackMediaStreamingSuccess({
        device_id: device.deviceId,
        device_brand_id: device.deviceBrandId,
        device_model_id: device.deviceModelId,
        media_type: 'video',
        surface: 'add_device'
      });

      return {
        // Read from the canonical registry rather than restated, so a rename that
        // moved a stage would change what the probe reports it drove.
        stages: Object.values(WEB_FUNNEL_STAGE_EVENTS),
        device
      };
    }
  };
};

if (isBrowserE2eEnabled()) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (globalThis as any).__irisWebFunnelProbe = createWebFunnelProbe();
}
